// frontend/source/portal/components/CreateArtistCard.js
import React, { useState } from "react";
import ImagePicker from "./ImagePicker";
import ConfirmDialog from "./ConfirmDialog";
import "./CreateArtistCard.css";

const CreateArtistCard = ({ onCreate }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [fname, setFname] = useState("");
  const [lname, setLname] = useState("");
  const [gender, setGender] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const handleStartCreate = () => {
    setIsCreating(true);
  };

  const handleCancel = () => {
    // Check if user has entered any data
    if (
      fname.trim() !== "" ||
      lname.trim() !== "" ||
      gender !== "" ||
      birthdate !== "" ||
      selectedImage
    ) {
      setShowCancelConfirm(true);
    } else {
      resetForm();
    }
  };

  const confirmCancel = () => {
    resetForm();
    setShowCancelConfirm(false);
  };

  const resetForm = () => {
    setIsCreating(false);
    setFname("");
    setLname("");
    setGender("");
    setBirthdate("");
    setSelectedImage(null);
  };

  const handleSave = async () => {
    if (fname.trim() === "") {
      alert("First name cannot be empty");
      return;
    }

    if (lname.trim() === "") {
      alert("Last name cannot be empty");
      return;
    }

    if (!selectedImage) {
      alert("Please select an image for the artist");
      return;
    }

    try {
      setIsSaving(true);
      await onCreate({
        fname: fname.trim(),
        lname: lname.trim(),
        gender: gender || undefined,
        birthdate: birthdate || undefined,
        imageUrl: selectedImage,
      });
      resetForm();
    } catch (err) {
      console.error("Failed to create artist:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleImageSelect = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  if (!isCreating) {
    return (
      <div className="create-artist-card" onClick={handleStartCreate}>
        <div className="create-artist-placeholder">
          <div className="create-artist-icon">+</div>
          <h2 className="create-artist-text">Add New Artist</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="create-artist-card creating">
      <div className="create-artist-card-image-container">
        {selectedImage ? (
          <>
            <img
              src={selectedImage}
              alt="Selected artist"
              className="create-artist-card-image"
            />
            <button
              onClick={() => setShowImagePicker(true)}
              className="create-artist-change-image-btn"
              title="Change image"
              disabled={isSaving}
            >
              📷
            </button>
          </>
        ) : (
          <div
            className="create-artist-image-placeholder"
            onClick={() => setShowImagePicker(true)}
          >
            <div className="create-artist-image-icon">🖼️</div>
            <p>Click to select image</p>
          </div>
        )}
      </div>

      <div className="create-artist-card-overlay">
        <div className="create-artist-input-container">
          <div className="create-artist-name-row">
            <input
              type="text"
              value={fname}
              onChange={(e) => setFname(e.target.value)}
              onKeyDown={handleKeyPress}
              className="create-artist-input"
              placeholder="First name"
              autoFocus
              disabled={isSaving}
            />
            <input
              type="text"
              value={lname}
              onChange={(e) => setLname(e.target.value)}
              onKeyDown={handleKeyPress}
              className="create-artist-input"
              placeholder="Last name"
              disabled={isSaving}
            />
          </div>

          <div className="create-artist-details-row">
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="create-artist-select"
              disabled={isSaving}
            >
              <option value="">Gender (optional)</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
            <input
              type="date"
              value={birthdate}
              onChange={(e) => setBirthdate(e.target.value)}
              className="create-artist-input"
              placeholder="Birthdate"
              disabled={isSaving}
            />
          </div>

          <div className="create-artist-buttons">
            <button
              onClick={handleSave}
              className="create-artist-btn save-btn"
              disabled={
                isSaving || !fname.trim() || !lname.trim() || !selectedImage
              }
            >
              {isSaving ? "Creating..." : "Create"}
            </button>
            <button
              onClick={handleCancel}
              className="create-artist-btn cancel-btn"
              disabled={isSaving}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {showImagePicker && (
        <ImagePicker
          currentImage={selectedImage}
          onImageSelect={handleImageSelect}
          onClose={() => setShowImagePicker(false)}
          imageType="students"
        />
      )}

      <ConfirmDialog
        isOpen={showCancelConfirm}
        title="Discard Changes?"
        message="You have unsaved changes. Are you sure you want to cancel creating this artist?"
        confirmText="Discard"
        cancelText="Keep Editing"
        type="warning"
        onConfirm={confirmCancel}
        onCancel={() => setShowCancelConfirm(false)}
      />
    </div>
  );
};

export default CreateArtistCard;
